import { useState, useEffect } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    Tabs,
    Tab,
    Avatar,
    Typography,
    IconButton,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';

const languages = ['hy', 'en', 'ru'];
const languageLabels = {
    hy: 'Armenian',
    en: 'English',
    ru: 'Russian',
};

const textFields = ['name', 'address'];

const emptyForm = () => {
    const initial = { phone: '', maps_url: '', sort_order: 0 };
    languages.forEach((lang) => {
        textFields.forEach((field) => {
            initial[`${field}_${lang}`] = '';
        });
    });
    return initial;
};

export default function BranchModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [sectionTab, setSectionTab] = useState(0);
    const [activeTab, setActiveTab] = useState(0);
    const [form, setForm] = useState(emptyForm);
    const [createdId, setCreatedId] = useState(null);
    const [mediaItems, setMediaItems] = useState([]);
    const [pendingImages, setPendingImages] = useState([]);
    const [pendingVideos, setPendingVideos] = useState([]);
    const [videoUrl, setVideoUrl] = useState('');
    const [posterFile, setPosterFile] = useState(null);
    const [posterPreview, setPosterPreview] = useState(null);
    const [mediaBusy, setMediaBusy] = useState(false);

    const branchId = row?.id || createdId;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const appendMedia = (items) => {
        if (!Array.isArray(items) || items.length === 0) return;
        setMediaItems((prev) => [...prev, ...items]);
    };

    const uploadMedia = async (id, payload) => {
        const formData = new FormData();
        (payload.images || []).forEach((file) => formData.append('images[]', file));
        (payload.videos || []).forEach((file) => formData.append('videos[]', file));
        if (payload.videoUrl) {
            formData.append('video_url', payload.videoUrl);
        }
        if (payload.poster) {
            formData.append('poster', payload.poster);
        }

        const response = await api.post(`admin/branches/${id}/media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        appendMedia(response.data.data);
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setErrors({});

            const formData = new FormData();
            languages.forEach((lang) => {
                textFields.forEach((field) => {
                    const key = `${field}_${lang}`;
                    formData.append(key, form[key] || '');
                });
            });
            formData.append('phone', form.phone || '');
            formData.append('maps_url', form.maps_url || '');
            formData.append('sort_order', form.sort_order === '' || form.sort_order === null ? 0 : form.sort_order);

            let savedId = branchId;
            if (savedId) {
                await api.post(`admin/branches/${savedId}/update`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                const response = await api.post("admin/branches", formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                savedId = response.data.data.id;
                setCreatedId(savedId);
            }

            const hasPending = pendingImages.length > 0 || pendingVideos.length > 0 || (videoUrl && posterFile);
            if (!row && hasPending) {
                await uploadMedia(savedId, {
                    images: pendingImages,
                    videos: pendingVideos,
                    videoUrl: videoUrl && posterFile ? videoUrl : '',
                    poster: videoUrl && posterFile ? posterFile : null,
                });
                setPendingImages([]);
                setPendingVideos([]);
                setVideoUrl('');
                setPosterFile(null);
                setPosterPreview(null);
            }

            onClose(true);
        } catch (e) {
            if (e.errors) {
                setErrors(e.errors);
            } else if (e.response?.data?.errors) {
                setErrors(e.response.data.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleAddFiles = (kind, fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        if (kind === 'images') {
            setPendingImages((prev) => [...prev, ...files]);
        } else {
            setPendingVideos((prev) => [...prev, ...files]);
        }
    };

    const handleUploadNow = async (kind, fileList) => {
        const files = Array.from(fileList || []);
        if (!branchId || files.length === 0) return;
        try {
            setMediaBusy(true);
            setErrors({});
            await uploadMedia(branchId, kind === 'images' ? { images: files } : { videos: files });
        } catch (e) {
            if (e.errors) setErrors(e.errors);
            else if (e.response?.data?.errors) setErrors(e.response.data.errors);
        } finally {
            setMediaBusy(false);
        }
    };

    const handleAddVideoUrl = async () => {
        if (!videoUrl || !posterFile) {
            setErrors({ video_url: ['Video URL and a poster image are both required'] });
            return;
        }
        if (!branchId) return;
        try {
            setMediaBusy(true);
            setErrors({});
            await uploadMedia(branchId, { videoUrl, poster: posterFile });
            setVideoUrl('');
            setPosterFile(null);
            setPosterPreview(null);
        } catch (e) {
            if (e.errors) setErrors(e.errors);
            else if (e.response?.data?.errors) setErrors(e.response.data.errors);
        } finally {
            setMediaBusy(false);
        }
    };

    const handleDeleteMedia = async (mediaId) => {
        if (!branchId) return;
        try {
            setMediaBusy(true);
            await api.delete(`admin/branches/${branchId}/media/${mediaId}`);
            setMediaItems((prev) => prev.filter((item) => item.id !== mediaId));
        } catch (e) {
            console.error(e);
        } finally {
            setMediaBusy(false);
        }
    };

    const handlePosterChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setPosterFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setPosterPreview(reader.result);
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        if (!open) return;

        if (row) {
            const newForm = emptyForm();
            languages.forEach((lang) => {
                textFields.forEach((field) => {
                    newForm[`${field}_${lang}`] = row[`${field}_${lang}`] || '';
                });
            });
            newForm.phone = row.phone || '';
            newForm.maps_url = row.maps_url || '';
            newForm.sort_order = row.sort_order ?? 0;
            setForm(newForm);
            setMediaItems(Array.isArray(row.media) ? row.media : []);
        } else {
            setForm(emptyForm());
            setMediaItems([]);
        }
        setErrors({});
        setSaving(false);
        setSectionTab(0);
        setActiveTab(0);
        setCreatedId(null);
        setPendingImages([]);
        setPendingVideos([]);
        setVideoUrl('');
        setPosterFile(null);
        setPosterPreview(null);
    }, [row, open]);

    const currentLanguage = languages[activeTab];
    const fieldError = (key) => errors[key]?.[0];

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
            <DialogTitle>{!row ? "Create Branch" : "Edit Branch"}</DialogTitle>
            <DialogContent>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={sectionTab} onChange={(e, value) => setSectionTab(value)}>
                        <Tab label="Details" />
                        <Tab label="Media" />
                    </Tabs>
                </Box>

                {sectionTab === 0 && (
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                                {languages.map((lang) => (
                                    <Tab key={lang} label={languageLabels[lang]} />
                                ))}
                            </Tabs>
                        </Box>

                        <FormControl error={!!fieldError(`name_${currentLanguage}`)}>
                            <FormLabel>Name ({languageLabels[currentLanguage]}) *</FormLabel>
                            <OutlinedInput
                                name={`name_${currentLanguage}`}
                                fullWidth
                                value={form[`name_${currentLanguage}`] || ''}
                                onChange={handleChange}
                            />
                            {fieldError(`name_${currentLanguage}`) && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError(`name_${currentLanguage}`)}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError(`address_${currentLanguage}`)}>
                            <FormLabel>Address ({languageLabels[currentLanguage]}) *</FormLabel>
                            <OutlinedInput
                                name={`address_${currentLanguage}`}
                                fullWidth
                                multiline
                                rows={3}
                                value={form[`address_${currentLanguage}`] || ''}
                                onChange={handleChange}
                            />
                            {fieldError(`address_${currentLanguage}`) && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError(`address_${currentLanguage}`)}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError('phone')}>
                            <FormLabel>Phone *</FormLabel>
                            <OutlinedInput
                                name="phone"
                                fullWidth
                                value={form.phone || ''}
                                onChange={handleChange}
                            />
                            {fieldError('phone') && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError('phone')}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError('maps_url')}>
                            <FormLabel>Map link</FormLabel>
                            <OutlinedInput
                                name="maps_url"
                                fullWidth
                                placeholder="https://"
                                value={form.maps_url || ''}
                                onChange={handleChange}
                            />
                            {fieldError('maps_url') && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError('maps_url')}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError('sort_order')}>
                            <FormLabel>Sort order</FormLabel>
                            <OutlinedInput
                                name="sort_order"
                                type="number"
                                fullWidth
                                value={form.sort_order}
                                onChange={handleChange}
                            />
                            {fieldError('sort_order') && (
                                <FormHelperText sx={{ m: 0.5 }} error>{fieldError('sort_order')}</FormHelperText>
                            )}
                        </FormControl>

                        <Typography variant="caption" color="text.secondary">
                            Email is shared across branches and comes from the contact page.
                        </Typography>
                    </Box>
                )}

                {sectionTab === 1 && (
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        {!branchId && (
                            <Typography variant="body2" color="text.secondary">
                                Files selected here are uploaded when you create the branch.
                            </Typography>
                        )}

                        {fieldError('media') && (
                            <FormHelperText error>{fieldError('media')}</FormHelperText>
                        )}

                        {mediaItems.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {mediaItems.map((item) => (
                                    <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {item.type === 'image' || item.video_url ? (
                                            <Avatar variant="rounded" src={item.url} sx={{ width: 64, height: 64 }} />
                                        ) : (
                                            <Avatar variant="rounded" sx={{ width: 64, height: 64 }}>Video</Avatar>
                                        )}
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="body2">
                                                {item.type === 'video' ? 'Video' : 'Image'}
                                            </Typography>
                                            {item.video_url && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.video_url}
                                                </Typography>
                                            )}
                                        </Box>
                                        {branchId && (
                                            <IconButton aria-label="Delete media" onClick={() => handleDeleteMedia(item.id)} disabled={mediaBusy}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}

                        <FormControl>
                            <FormLabel>Photos</FormLabel>
                            <Button variant="outlined" component="label" disabled={mediaBusy} sx={{ mt: 1, alignSelf: 'flex-start' }}>
                                {branchId ? 'Upload Photos' : 'Select Photos'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    multiple
                                    onChange={(e) => {
                                        if (branchId) handleUploadNow('images', e.target.files);
                                        else handleAddFiles('images', e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                            </Button>
                            {!branchId && pendingImages.length > 0 && (
                                <Typography variant="caption">{pendingImages.length} photo(s) selected</Typography>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError('videos') || !!(errors['videos.0'])}>
                            <FormLabel>Video files</FormLabel>
                            <Button variant="outlined" component="label" disabled={mediaBusy} sx={{ mt: 1, alignSelf: 'flex-start' }}>
                                {branchId ? 'Upload Videos' : 'Select Videos'}
                                <input
                                    type="file"
                                    hidden
                                    accept="video/mp4,video/webm"
                                    multiple
                                    onChange={(e) => {
                                        if (branchId) handleUploadNow('videos', e.target.files);
                                        else handleAddFiles('videos', e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                            </Button>
                            {!branchId && pendingVideos.length > 0 && (
                                <Typography variant="caption">{pendingVideos.length} video(s) selected</Typography>
                            )}
                        </FormControl>

                        <FormControl error={!!fieldError('video_url') || !!fieldError('poster')}>
                            <FormLabel>Video URL</FormLabel>
                            <OutlinedInput
                                fullWidth
                                placeholder="https://"
                                value={videoUrl}
                                onChange={(e) => setVideoUrl(e.target.value)}
                                sx={{ mt: 1 }}
                            />
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                                {posterPreview && (
                                    <Avatar variant="rounded" src={posterPreview} sx={{ width: 64, height: 64 }} />
                                )}
                                <Button variant="outlined" component="label">
                                    Poster image
                                    <input type="file" hidden accept="image/*" onChange={handlePosterChange} />
                                </Button>
                                {branchId && (
                                    <Button variant="contained" onClick={handleAddVideoUrl} disabled={mediaBusy || !videoUrl || !posterFile}>
                                        Add
                                    </Button>
                                )}
                            </Box>
                            {!branchId && videoUrl && posterFile && (
                                <Typography variant="caption">Video URL will be saved with the branch.</Typography>
                            )}
                            {(fieldError('video_url') || fieldError('poster')) && (
                                <FormHelperText error>{fieldError('video_url') || fieldError('poster')}</FormHelperText>
                            )}
                        </FormControl>

                        {mediaBusy && <CircularProgress size={24} />}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                    sx={{ color: '#fff' }}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={24} /> : (!row ? "Create" : "Update")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
